// Profile.js with URL that can't be edited after creation

import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { SketchPicker } from "react-color";

import {
  Box,
  Button,
  Divider as MuiDivider,
  Grid,
  TextField,
  Typography,
  Avatar as MuiAvatar,
  InputAdornment,
  Card as MuiCard,
  CardContent,
  Paper,
  CircularProgress,
  Tooltip,
  InputLabel,
  FormControl,
  OutlinedInput,
  FormHelperText,
} from "@mui/material";
import {
  CloudUpload as MuiCloudUpload,
  Info as InfoIcon,
} from "@mui/icons-material";
import { spacing } from "@mui/system";

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const CloudUpload = styled(MuiCloudUpload)(spacing);
const Avatar = styled(MuiAvatar)`
  background: ${(props) => props.theme.palette.primary.main};
`;

const CenteredContent = styled.div`
  text-align: center;
`;

const BigAvatar = styled(Avatar)`
  width: 120px;
  height: 120px;
  margin: 0 auto ${(props) => props.theme.spacing(2)};
`;

// Maximum file size - 10MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;
// Optimal file size for compression - 1MB
const OPTIMAL_FILE_SIZE = 1 * 1024 * 1024;
// Allowed file types
const ALLOWED_FILE_TYPES = ["image/jpeg", "image/png", "image/gif"];

// Function to compress image
const compressImage = async (file) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement("canvas");
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions while maintaining aspect ratio
        if (width > height) {
          if (width > 800) {
            height = Math.round((height * 800) / width);
            width = 800;
          }
        } else {
          if (height > 800) {
            width = Math.round((width * 800) / height);
            height = 800;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to JPEG with quality adjustment
        const quality = 0.7; // Adjust quality to reduce file size
        const base64String = canvas.toDataURL("image/jpeg", quality);
        resolve(base64String);
      };
    };
  });
};

function Profile({ team, onUpdate }) {
  const [formData, setFormData] = useState({
    name: team?.name || "",
    url: team?.url || "",
    about: team?.about || "",
  });
  const [logo, setLogo] = useState(team?.logo || null);
  const [logoPreview, setLogoPreview] = useState(team?.logo || "");
  const [color, setColor] = useState(team?.color || "#2196F3");
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [isFormChanged, setIsFormChanged] = useState(false);
  const [logoError, setLogoError] = useState("");
  const [uploading, setUploading] = useState(false);

  // Determine if the team already has a URL (for read-only check)
  const isExistingTeam = Boolean(team?.url);

  // Handle logo file upload using base64 conversion
  const handleLogoChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLogoError("");

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setLogoError("File size should not exceed 10MB");
      return;
    }

    // Validate file type
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setLogoError("Please upload a valid image file (JPEG, PNG, or GIF)");
      return;
    }

    try {
      // Show loading state for large images
      if (file.size > OPTIMAL_FILE_SIZE) {
        setUploading(true);
      }

      // Compress image if it's larger than optimal size
      let imageData;
      if (file.size > OPTIMAL_FILE_SIZE) {
        imageData = await compressImage(file);
      } else {
        imageData = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      }

      // Set both the preview and the actual logo data
      setLogoPreview(imageData);
      setLogo(imageData);
      setIsFormChanged(true);
    } catch (error) {
      console.error("Image processing error:", error);
      setLogoError("Failed to process image. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setIsFormChanged(true);
  };

  // Handle URL input with formatting
  const handleUrlChange = (e) => {
    // Only allow changes if this is a new team (no existing URL)
    if (isExistingTeam) return;

    // Convert to lowercase and replace spaces with dashes
    const value = e.target.value
      .toLowerCase()
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "");

    setFormData({ ...formData, url: value });
    setIsFormChanged(true);
  };

  // Handle color changes
  const handleColorChange = (newColor) => {
    setColor(newColor.hex);
    setIsFormChanged(true);
  };

  // Handle form submission
  const handleSubmit = async () => {
    const updateData = {
      name: formData.name,
      url: formData.url,
      about: formData.about,
      color: color,
      logo: logo, // Use the base64 image data
    };

    onUpdate(updateData);
    setIsFormChanged(false);
  };

  return (
    <Box>
      <Typography variant="h3" gutterBottom>
        Team Profile
      </Typography>
      <Typography color="textSecondary" mb={4}>
        Manage your team's basic information
      </Typography>

      <Paper sx={{ p: 3, mb: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <CenteredContent>
              {logoPreview ? (
                <BigAvatar
                  src={logoPreview}
                  alt={formData.name}
                  sx={{ bgcolor: color }}
                />
              ) : (
                <BigAvatar sx={{ bgcolor: color }}>
                  {formData.name ? formData.name.charAt(0).toUpperCase() : "T"}
                </BigAvatar>
              )}
              <input
                accept="image/*"
                style={{ display: "none" }}
                id="team-logo-upload"
                type="file"
                onChange={handleLogoChange}
                disabled={uploading}
              />
              <label htmlFor="team-logo-upload">
                <Button
                  variant="contained"
                  color="primary"
                  component="span"
                  startIcon={<CloudUpload />}
                  disabled={uploading}
                >
                  {uploading ? (
                    <>
                      Uploading...
                      <CircularProgress
                        size={24}
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          marginTop: "-12px",
                          marginLeft: "-12px",
                          color: "white",
                        }}
                      />
                    </>
                  ) : (
                    "Upload Team Logo"
                  )}
                </Button>
              </label>
              {logoError && (
                <Typography
                  variant="caption"
                  color="error"
                  display="block"
                  mt={1}
                >
                  {logoError}
                </Typography>
              )}
              <Typography variant="caption" display="block" gutterBottom mt={1}>
                For best results, use a square image at least 128px by 128px in
                .jpg format
              </Typography>
            </CenteredContent>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Team Name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              required
              helperText="Your team's display name"
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl variant="outlined" fullWidth>
              <InputLabel htmlFor="team-url">Team URL</InputLabel>
              <OutlinedInput
                id="team-url"
                value={formData.url}
                onChange={handleUrlChange}
                startAdornment={
                  <InputAdornment position="start">
                    <Typography color="textSecondary">
                      instaconnect.com/team/
                    </Typography>
                  </InputAdornment>
                }
                endAdornment={
                  isExistingTeam && (
                    <InputAdornment position="end">
                      <Tooltip title="Team URL cannot be changed after creation">
                        <InfoIcon color="action" />
                      </Tooltip>
                    </InputAdornment>
                  )
                }
                label="Team URL"
                readOnly={isExistingTeam}
                inputProps={{
                  readOnly: isExistingTeam,
                }}
                sx={{
                  backgroundColor: isExistingTeam
                    ? "rgba(0, 0, 0, 0.04)"
                    : "transparent",
                }}
              />
              <FormHelperText>
                {isExistingTeam
                  ? "Team URL cannot be changed after creation"
                  : "This will be the URL for your team's public page. Only lowercase letters, numbers, and hyphens are allowed."}
              </FormHelperText>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="About"
              name="about"
              value={formData.about}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              helperText="Briefly describe your team (optional)"
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Team Color
            </Typography>
            <Box display="flex" alignItems="center">
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                  mr: 2,
                  bgcolor: color,
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  cursor: "pointer",
                }}
                onClick={() => setColorPickerOpen(!colorPickerOpen)}
              />
              <TextField
                value={color}
                onChange={(e) => {
                  setColor(e.target.value);
                  setIsFormChanged(true);
                }}
                variant="outlined"
                size="small"
                sx={{ width: 120 }}
              />
            </Box>
            {colorPickerOpen && (
              <Box sx={{ mt: 2, position: "relative", zIndex: 1 }}>
                <Box
                  sx={{
                    position: "fixed",
                    top: 0,
                    right: 0,
                    bottom: 0,
                    left: 0,
                  }}
                  onClick={() => setColorPickerOpen(false)}
                />
                <Box sx={{ position: "relative", zIndex: 2 }}>
                  <SketchPicker
                    color={color}
                    onChangeComplete={handleColorChange}
                  />
                </Box>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={
                  !isFormChanged || !formData.name || !formData.url || uploading
                }
              >
                Save Changes
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default Profile;
